import { defineStore } from 'pinia'
import { useCookies } from 'vue3-cookies'
import { useStorage } from '@/composables/use-storage'
import { LocalStorageKey } from '@/enums/local-storage-key'
import { Locale } from '@/enums/locale'
import i18n from '@/locales'

export const useAuthStore = defineStore('auth', () => {
  const { cookies } = useCookies()
  const { setStorageData, removeStorageData } = useStorage()

  const TOKEN = '_token'
  const tokenData = ref<string | null>(null)
  const LANG = '_lang'
  const lang = ref<Locale | null>(null)

  const isTokenEmpty = computed(() => {
    return !tokenData.value
  })

  /**
   * Cookies 的 Token 是否為空
   */
  const isCookiesTokenEmpty = (): boolean => {
    return !cookies.get(TOKEN)
  }

  /**
   * Cookies 的 Token 是否過期
   */
  const isTokenExpired = (token: string) => {
    try {
      // 將 JWT 的第二部分（Payload）解碼
      const payloadBase64 = token.split('.')[1]

      if (!payloadBase64) {
        throw new Error('Invalid token')
      }
      const payloadJson = atob(payloadBase64) // 解碼 Base64
      const payload = JSON.parse(payloadJson)

      // 取得當前時間（單位：秒）
      const currentTime = Math.floor(Date.now() / 1000)

      // 檢查是否過期
      return payload.exp && payload.exp < currentTime
    } catch {
      return true // 若解析失敗，預設為已過期
    }
  }

  /**
   * 重設 Token 資料
   */
  const resetTokenData = (): void => {
    tokenData.value = null
    cookies.remove(TOKEN)
  }

  /**
   * 設定 Token 資料
   */
  const setTokenData = (data: string): void => {
    tokenData.value = data
    cookies.set(TOKEN, data)
  }

  // const setTokenCookies = (): void => {
  //   if (tokenData.value) {
  //     cookies.set(TOKEN, tokenData.value)
  //   }
  // }

  /**
   * 從 Cookies 取得 Token 資料，並寫入 Store
   */

  const setTokenDataByCookies = (): boolean => {
    const token = cookies.get(TOKEN)
    if (isTokenExpired(token)) {
      resetTokenData()
      return false
    }

    tokenData.value = token
    return !!tokenData.value
  }

  const isLangEmpty = computed(() => {
    return !lang.value
  })

  /**
   * 設定 Lang 資料
   */
  const setLang = (data: Locale): void => {
    lang.value = data
    cookies.set(LANG, data)
    setI18nLang(data)
  }

  const setLangByCookies = () => {
    const cookiesLang = cookies.get(LANG)
    lang.value = cookiesLang as Locale
    setI18nLang(lang.value)
  }

  const setI18nLang = (data: Locale): void => {
    i18n.global.locale.value =
      data === Locale.ZH_TW ? Locale.ZH_TW : Locale.EN_US
    setStorageData(LocalStorageKey.LOCALE, data)
  }

  /**
   * 重設 Lang 資料
   */
  const resetLang = (): void => {
    lang.value = null
    cookies.remove(LANG)
    removeStorageData(LocalStorageKey.LOCALE)
  }

  return {
    isTokenEmpty,
    tokenData,
    isLangEmpty,
    lang,
    isCookiesTokenEmpty,
    resetTokenData,
    setTokenData,
    setTokenDataByCookies,
    setLang,
    resetLang,
    setLangByCookies
  }
})
